'use client';

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useReactToPrint } from 'react-to-print';
import useAccount from '@/lib/hooks/useAccount';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import Button from '@/components/atoms/project-builder/button';
import ShareProjectPopup from '@/components/organisms/project-builder/share-project-popup';
import Spinner from '@/components/organisms/spinner';
import Typography from '@/components/atoms/project-builder/typography';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import SummaryTable from './landscapeSummaryTable';
import ExportToExcelAndCsv from './exportToExcelAndCsv';
import ProductCard from './productCard';
import PortraitSummaryTable from './portraitSummaryTable';
import SummaryBanner from './summaryBanner';
import TemplateToggle from './TemplateToggle';
import { generatePdfForProjectFromHtml, downloadProjectPdf } from '@/helpers/next-services';
import { exportBlobAsFile } from '@/helpers/utils/downloadExcelSheet';
import { PRINT_BUTTON_CLICK, SAVE_BUTTON_CLICK } from '@/helpers/constants/events';

const ProjectSummary: React.FC<any> = ({ projectFormData, handleTabClick, allowShare, data, selectDataForPublishedProducts}: any) => {
  const { locale } = useParams();
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [isLandscapeMode, setLandscapeMode] = useState(projectFormData?.showLandscape || false);
  const [baseUrl, setBaseUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const { account } = useAccount();
  const { updateProjectStatus } = useProjectBuilder();
  const { projectName, roomGroups, showPricesOnPdf } = projectFormData;

  const tableData = useMemo(() => {
    const headers = ['Room', 'Prod Code', 'Product Name', 'Qty'];
    if (showPricesOnPdf) headers.push('Price');
    return { headers };
  }, [showPricesOnPdf]);

  const handlePrint = useReactToPrint({ contentRef: pageRef });

  const reactToPrintFn = () => {
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: PRINT_BUTTON_CLICK,
      'project name': projectName,
    });
    const style = document.createElement('style');
    style.innerHTML = `@page { size: ${isLandscapeMode ? 'Landscape;' : 'Portrait;'} }`;
    style.id = 'page-orientation';
    document.head.appendChild(style);
    handlePrint();
  };

  const generatePDF = async () => {
    if (!pageRef.current) return;
    try {
      setLoading(true);
      // @ts-ignore
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      window.dataLayer.push({
          event: SAVE_BUTTON_CLICK,
          'project name': projectName,
      });
      let html = pageRef.current.innerHTML
      .replace(/\s+srcset\s*=\s*(['"])(?:(?!\1).)*\1/gi, '')
      .replace(/\s*alt=""/g, '')
      .replace(/\s*title=""/g, '')
      .replace(/\s*loading="lazy"/g, '');
      const largeTextToRemove ='<div class="mt-5 flex justify-center print:hidden"><button type="button" data-html2canvas-ignore="true" class="py-12 px-24 rounded-md   relative text-14 leading-4 transition overflow-hidden font-semibold bg-white-100 text-gray-700 bg-white rounded-md border border-secondary-black hover:shadow-bottom active:outline-2 active:shadow-button active:disabled:shadow-none active:outline-1 active:outline-gray-300 active:outline-offset-0 focus-visible:outline focus-visible:border-white-100 focus-visible:border focus-visible:outline-offset-[3px] disabled:text-neutral-100 disabled:bg-neutral-400 px-8 py-8 text-sm font-semibold cursor-pointer">View Product Detail Page</button></div>';
      html = html.split(largeTextToRemove).join('');
      filename = `${projectName.replace(/\s+/g, '_')}_summary_${generateRandomCode(4)}.pdf`;
      generatePdfForProjectFromHtml({ html, isLandscapeMode, locale, filename, shoppingListId: projectFormData?.shoppingListId || '', emailId:account?.email || '' });
      checkForProjectPdf();

    } catch (error) {
      console.error('Error generating PDF:', error);
      setLoading(false);
    } finally {
      
    }
  };

  const generateRandomCode = (length: number) => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      result += chars[randomIndex];
    }
    return result;
  }
    
  let filename = '';
  const checkForProjectPdf = async () => {
      try {
        const blob = await downloadProjectPdf(filename, locale);
        if(blob){
            exportBlobAsFile(blob, filename);
            setLoading(false);
        }
        else{
          setTimeout(checkForProjectPdf, 2000);
        }
      } catch (error) {
        console.error('Error downloading PDF:', error);
        setLoading(false);
      }
	}

  const handleToggleLandscape = async (val: boolean) => {
    setLandscapeMode(val);
    try {
      await updateProjectStatus({
        shoppingListId: projectFormData?.shoppingListId,
        version: projectFormData?.version,
        status: val,
        fieldName: 'showLandscape',
      });
    } catch (error) {
      console.error('Failed to update landscape mode:', error);
    }
  };

  const renderProductList = () => {
    return roomGroups?.map((room: any, roomIdx: number) => (
      <div key={roomIdx}>
        <h2 className="pt-[20px] text-2xl font-semibold print:pt-2.5 print:text-lg">{room.roomName}</h2>
        {room.items?.filter((item: any) => item.isProductAvailable).map((product: any, index: number) => {
          const isLast = roomIdx === roomGroups.length - 1 && index === room.items.length - 1;
          return (
            <div
              key={index}
              className={`${!product.isProductAvailable ? 'print:hidden' : ''} ${!isLast ? 'break-after-page' : ''}`}
            >
              {!product.isProductAvailable && <ProductUnavailableNotice />}
              <div
                className={` ${index === 0 || !product.isProductAvailable ? 'print:!pt-0' : 'print:!pt-2.5'
                  } ${!product.isProductAvailable ? 'opacity-50 print:hidden' : ''}`}
              >
                <ProductCard
                  key={index}
                  {...product}
                  handleTabClick={handleTabClick}
                  allowShare={allowShare}
                  showPricesOnPdf={showPricesOnPdf}
                  isLandscapeMode={isLandscapeMode}
                  selectDataForPublishedProducts={selectDataForPublishedProducts}
                />
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setBaseUrl(new URL(window.location.href).origin);
    }
  }, []);

  if (!projectFormData) return null;

  const shareLink = `${baseUrl}/${locale}/project-builder/share-summary/?projectId=${projectFormData?.shoppingListId ?? ''
    }&company=${encodeURIComponent(account?.companyName ?? '')}`;

  return (
    <>
      {!allowShare && (
        <div className="p-2 xl:p-[20px] pb-0">
          <p className="!mb-[12px] text-base font-semibold text-[#090909]">Template preference</p>
          <TemplateToggle isLandscapeMode={isLandscapeMode} onToggle={handleToggleLandscape} />
          <ExportToExcelAndCsv projectFormData={projectFormData} selectDataForPublishedProducts={selectDataForPublishedProducts} />
        </div>
      )}

      <div className="p-2 xl:p-[20px]" ref={pageRef}>
        <div className="w-full">
          <SummaryBanner
            onShare={() => setShowSharePopup(true)}
            onDownload={generatePDF}
            onPrint={reactToPrintFn}
            data={data}
            projectFormData={projectFormData}
            allowShare={allowShare}
          />
          <div className="my-[20px] text-2xl font-semibold print:!mt-2.5 print:!mb-0 print:text-xl">Product Summary</div>
          {isLandscapeMode ? (
            <SummaryTable
              setLoading={setLoading}
              roomGroups={roomGroups}
              tableData={tableData}
              showPricesOnPdf={showPricesOnPdf}
              selectDataForPublishedProducts={selectDataForPublishedProducts}
            />
          ) : (
            <>
              <PortraitSummaryTable
                roomGroups={roomGroups}
                tableData={tableData}
                showPricesOnPdf={showPricesOnPdf}
                selectDataForPublishedProducts={selectDataForPublishedProducts}
              />
              {renderProductList()}
            </>
          )}

          {!allowShare && (
            <div className="mt-[20px] flex justify-between print:hidden" data-html2canvas-ignore>
              <Button className='!bg-[#1A1A1A] font-semibold !py-[12px] !px-[24px] cursor-pointer' onClick={() => handleTabClick(2, projectFormData)}>Back to products</Button>
            </div>
          )}

          <ShareProjectPopup shareLink={shareLink} show={showSharePopup} onClose={() => setShowSharePopup(false)} />
        </div>
      </div>

      {loading && <><Spinner />
    <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
      </>}
    </>
  );
};

export default ProjectSummary;

export const ProductUnavailableNotice: React.FC = () => {
  return (
    <div className="w-full pt-20 shadow-none print:hidden print:!pt-0">
      <div>
        <div className="grid w-full justify-between p-8 print:!p-0 sm:flex">
                  <div className="mb-[12px] flex items-start space-x-2 print:!mb-0 print:pt-2.5">
          <InformationCircleIcon className="max-w-64 h-6 max-h-64 w-6 flex-shrink-0 stroke-[#F27320] text-[#F27320] print:!h-4 print:!w-4" />
          <Typography className="ml-2.5 text-base text-[#F27320] print:!pt-0 print:!text-xs">
              This product you have selected may no longer be available.
            </Typography>
          </div>
          <Button
            onClick={() => {
              window.location.href = '/';
            }}
            className="!bg-[#A6C9CC] text-black print:hidden"
          >
            View recommended alternatives
          </Button>
        </div>
      </div>
    </div>
  );
};